import { CardData, DeckObject, QuestionGenerationTemplateId } from "../../constants/GlobalTypesAndEnumsAndConstants";

type QuestionGenerationTrackingProperties = {
  question_generation_worker?: string;
  question_generation_template_id?: QuestionGenerationTemplateId | string;
  is_step1_question?: boolean;
  is_mcat_question?: boolean;
  is_table_question?: boolean;
  assessment_unit_kind?: string;
  assessment_unit_question_count?: number;
  mcat_section?: string;
  mcat_reasoning_skill?: string;
  suggested_exam_type?: string;
};

type ExistingQuestionGenerationTrackingProperties = Record<string, unknown>;

type AnswerContentTrackingSource = Pick<
  CardData,
  "answer" | "question" | "questionVersionBeingUsed" | "answerVersionBeingUsed" | "question_generation_template_id"
>;

type DeckQuestionGenerationTrackingSource = Pick<DeckObject, "generation_metadata">;

type AnsweredCardQuestionGenerationTrackingSource = Pick<
  CardData,
  | "question_generation_worker"
  | "question_generation_template_id"
  | "assessment_unit_kind"
  | "assessment_unit_question_count"
  | "assessment_unit_exam_tags"
  | "exam_tags"
>;

export function getDeckQuestionGenerationTrackingProperties(
  currentReviewDeck: DeckQuestionGenerationTrackingSource | null | undefined,
  existingEventTrackingProperties: ExistingQuestionGenerationTrackingProperties = {}
): QuestionGenerationTrackingProperties {
  const generationMetadata = currentReviewDeck?.generation_metadata;
  const trackingProperties: QuestionGenerationTrackingProperties = {};

  if (
    generationMetadata?.question_generation_worker &&
    existingEventTrackingProperties.question_generation_worker == null
  ) {
    trackingProperties.question_generation_worker = generationMetadata.question_generation_worker;
  }

  if (
    generationMetadata?.question_generation_template_id &&
    existingEventTrackingProperties.question_generation_template_id == null
  ) {
    trackingProperties.question_generation_template_id = generationMetadata.question_generation_template_id;
  }

  if (generationMetadata?.suggested_exam_type && existingEventTrackingProperties.suggested_exam_type == null) {
    trackingProperties.suggested_exam_type = generationMetadata.suggested_exam_type;
  }

  return trackingProperties;
}

export function getAnsweredCardQuestionGenerationTrackingProperties(
  currentCardData: AnsweredCardQuestionGenerationTrackingSource | null | undefined
): QuestionGenerationTrackingProperties {
  const questionGenerationTemplateId = currentCardData?.question_generation_template_id;
  const isMcatQuestion = questionGenerationTemplateId === QuestionGenerationTemplateId.MCAT;
  const isTableQuestion = questionGenerationTemplateId === QuestionGenerationTemplateId.TableMCQ;
  const assessmentUnitSection = currentCardData?.assessment_unit_exam_tags?.section;
  const reasoningSkill = currentCardData?.exam_tags?.reasoning_skill;

  return {
    question_generation_worker: currentCardData?.question_generation_worker,
    question_generation_template_id: questionGenerationTemplateId,
    is_step1_question: questionGenerationTemplateId === QuestionGenerationTemplateId.USMLEStep1,
    is_mcat_question: isMcatQuestion,
    is_table_question: isTableQuestion,
    assessment_unit_kind: isMcatQuestion || isTableQuestion ? currentCardData?.assessment_unit_kind : undefined,
    assessment_unit_question_count: isMcatQuestion || isTableQuestion ? currentCardData?.assessment_unit_question_count : undefined,
    mcat_section: isMcatQuestion && typeof assessmentUnitSection === "string" ? assessmentUnitSection : undefined,
    mcat_reasoning_skill: isMcatQuestion && typeof reasoningSkill === "string" ? reasoningSkill : undefined,
  };
}

export function getAnswerContentTrackingProperties(
  currentCardData: AnswerContentTrackingSource | null | undefined
): Record<string, string | undefined> {
  if (currentCardData?.question_generation_template_id === QuestionGenerationTemplateId.MCAT ||
    currentCardData?.question_generation_template_id === QuestionGenerationTemplateId.TableMCQ) {
    return {};
  }
  return {
    correctAnswer: currentCardData?.answer,
    question: currentCardData?.question,
    displayedQuestion: currentCardData?.questionVersionBeingUsed,
    displayedAnswer: currentCardData?.answerVersionBeingUsed,
  };
}

export function buildAnswerSubmittedForFeedbackTrackingProperties(
  baseProperties: Record<string, unknown>,
  currentCardData: Pick<CardData, "question_generation_template_id"> | null | undefined,
  answerChoice: string,
  isSelectedMultipleChoiceAnswerCorrect: boolean,
  wasAnsweredWithKeyPress: boolean
): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    ...baseProperties,
    userAnswerInput: answerChoice,
    isSelectedMultipleChoiceAnswerCorrect,
    wasAnsweredWithKeyPress,
  };
  if (currentCardData?.question_generation_template_id === QuestionGenerationTemplateId.TableMCQ) {
    // Generated table content can contain source material; retain only metadata.
    for (const key of ["userAnswerInput", "question", "correctAnswer", "displayedQuestion", "displayedAnswer"]) {
      delete properties[key];
    }
  }
  return properties;
}
